import { useMemo } from 'react';

import {
  CallDto,
  useCampaignCallListControllerCallListQuery,
  useCampaignWebhookCreateControllerCreateMutation,
  useCampaignWebhookDeleteControllerDeleteMutation,
  useCampaignWebhookDispatchControllerManualDispatchMutation,
  useCampaignWebhookUpdateControllerUpdateMutation,
  useCampaignWebhooksControllerListQuery,
} from '@shared/services/apiService/apiService';

import {
  CampaignWebhookDraft,
  buildCampaignWebhookCreateRequest,
  buildCampaignWebhookUpdateRequest,
  createEmptyCampaignWebhookDraft,
  normalizeCampaignWebhook,
  sortCampaignWebhooks,
} from '../utils/campaign-webhook-mapper';

const sortEndedCalls = (calls: CallDto[]): CallDto[] =>
  [...calls].sort((left, right) => {
    const leftTime = new Date(left.endedAt ?? left.createdAt).getTime();
    const rightTime = new Date(right.endedAt ?? right.createdAt).getTime();

    return rightTime - leftTime;
  });

export const useCampaignWebhooks = (campaignId?: string) => {
  const {
    data: webhookResponse,
    isLoading: isWebhooksLoading,
    isFetching: isWebhooksFetching,
    refetch: refetchWebhooks,
  } = useCampaignWebhooksControllerListQuery(
    { campaignId: campaignId! },
    {
      skip: !campaignId,
      refetchOnMountOrArgChange: true,
    },
  );

  const {
    data: endedCallsResponse,
    isLoading: isEndedCallsLoading,
    isFetching: isEndedCallsFetching,
    refetch: refetchEndedCalls,
  } = useCampaignCallListControllerCallListQuery(
    {
      id: campaignId!,
      statuses: 'ended',
    },
    {
      skip: !campaignId,
      refetchOnMountOrArgChange: true,
    },
  );

  const [createWebhook, { isLoading: isCreatingWebhook }] =
    useCampaignWebhookCreateControllerCreateMutation();
  const [updateWebhook, { isLoading: isUpdatingWebhook }] =
    useCampaignWebhookUpdateControllerUpdateMutation();
  const [deleteWebhookMutation, { isLoading: isDeletingWebhook }] =
    useCampaignWebhookDeleteControllerDeleteMutation();
  const [dispatchWebhookMutation, { isLoading: isDispatchingWebhook }] =
    useCampaignWebhookDispatchControllerManualDispatchMutation();

  const webhooks = useMemo(
    () => sortCampaignWebhooks((webhookResponse ?? []).map(normalizeCampaignWebhook)),
    [webhookResponse],
  );

  const endedCalls = useMemo(() => sortEndedCalls(endedCallsResponse ?? []), [endedCallsResponse]);

  const saveWebhook = async (draft: CampaignWebhookDraft): Promise<void> => {
    if (!campaignId) {
      throw new Error('Campaign id is required');
    }

    if (draft.id) {
      const campaignWebhookUpdateRequestDto = buildCampaignWebhookUpdateRequest(draft);

      if (Object.keys(campaignWebhookUpdateRequestDto).length === 0) {
        return;
      }

      await updateWebhook({
        campaignId,
        webhookId: draft.id,
        campaignWebhookUpdateRequestDto,
      }).unwrap();
    } else {
      await createWebhook({
        campaignId,
        campaignWebhookCreateRequestDto: buildCampaignWebhookCreateRequest(draft),
      }).unwrap();
    }

    await refetchWebhooks();
  };

  const deleteWebhook = async (webhookId: string): Promise<void> => {
    if (!campaignId) {
      throw new Error('Campaign id is required');
    }

    await deleteWebhookMutation({
      campaignId,
      webhookId,
    }).unwrap();

    await refetchWebhooks();
  };

  const dispatchWebhooks = async (callId: string): Promise<void> => {
    if (!campaignId) {
      throw new Error('Campaign id is required');
    }

    await dispatchWebhookMutation({
      campaignId,
      callId,
    }).unwrap();
  };

  return {
    webhooks,
    endedCalls,
    isLoading: isWebhooksLoading || isWebhooksFetching,
    isEndedCallsLoading: isEndedCallsLoading || isEndedCallsFetching,
    isSavingWebhook: isCreatingWebhook || isUpdatingWebhook,
    isDeletingWebhook,
    isDispatchingWebhook,
    createDraft: createEmptyCampaignWebhookDraft,
    saveWebhook,
    deleteWebhook,
    dispatchWebhooks,
    refetchWebhooks,
    refetchEndedCalls,
  };
};
